import { type FormSubmission, type Stored } from "@overmap-ai/common"
import { useMemo } from "react"

import { useAppSelector } from "../store"
import {
	selectAttachmentsByFormSubmissionFieldValues,
	selectLatestFieldValuesByFormSubmission,
	selectLatestIdentifierValuesByFormSubmission,
} from "../store/selectors/forms"
import type { FormSubmissionAndIdentifiers, FormSubmissionData } from "../typings"

export const useFormSubmissionData = (formSubmissions: Stored<FormSubmission>[]) => {
	const latestIdentifierValuesByFormSubmission = useAppSelector(selectLatestIdentifierValuesByFormSubmission)
	const latestFieldValuesByFormSubmission = useAppSelector(selectLatestFieldValuesByFormSubmission)
	const attachmentsByFormSubmissionFieldValues = useAppSelector(selectAttachmentsByFormSubmissionFieldValues)

	return useMemo(() => {
		const ret: FormSubmissionData[] = []

		for (const formSubmission of formSubmissions) {
			const formIdentifierValues = latestIdentifierValuesByFormSubmission[formSubmission.uuid] ?? {}
			const formSubmissionFieldValues = latestFieldValuesByFormSubmission[formSubmission.uuid]
			const formSubmissionFieldValuesAttachments =
				attachmentsByFormSubmissionFieldValues[formSubmissionFieldValues?.uuid ?? ""] ?? []

			ret.push({
				formSubmission,
				formIdentifierValues,
				formSubmissionFieldValues,
				formSubmissionFieldValuesAttachments,
			})
		}

		return ret
	}, [
		latestIdentifierValuesByFormSubmission,
		formSubmissions,
		latestFieldValuesByFormSubmission,
		attachmentsByFormSubmissionFieldValues,
	])
}

export const useFormSubmissionsWithIdentifiers = (
	formSubmissions: Stored<FormSubmission>[],
): FormSubmissionAndIdentifiers[] => {
	const latestIdentifierValuesByFormSubmission = useAppSelector(selectLatestIdentifierValuesByFormSubmission)

	return useMemo(() => {
		return formSubmissions.map((formSubmission) => ({
			formSubmission,
			identifierValues: Object.values(latestIdentifierValuesByFormSubmission[formSubmission.uuid] ?? {}),
		}))
	}, [latestIdentifierValuesByFormSubmission, formSubmissions])
}
